/* A chat's own knowledge set (`PATCH /threads/{id}` `knowledge_base_ids`). Split
   from `threads.ts`: one mutation with its own optimistic write. */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { threadKeys, updateThread } from "./threads";

import type { ApiThread, ThreadKnowledge } from "@/shared/types";

/** `ids`: a list is this chat's own set (`[]` = off); `null` follows the agent
    again. `bases` is what the screen shows until the server answers. */
export interface KnowledgeChoice {
  threadId: string;
  ids: string[] | null;
  bases: ThreadKnowledge["knowledge_bases"];
}

/** Shows the choice at once; a failed save puts the old read back. */
export function useSetThreadKnowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ threadId, ids }: KnowledgeChoice) => updateThread(threadId, { knowledge_base_ids: ids }),
    onMutate: async ({ threadId, ids, bases }) => {
      const key = threadKeys.one(threadId);
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData<ApiThread>(key);
      if (before) qc.setQueryData<ApiThread>(key, { ...before, knowledge: { overridden: ids !== null, knowledge_bases: bases } });
      return { before };
    },
    onError: (_error, { threadId }, context) => {
      if (context?.before) qc.setQueryData(threadKeys.one(threadId), context.before);
    },
    onSettled: (_data, _error, { threadId }) => qc.invalidateQueries({ queryKey: threadKeys.one(threadId) }),
  });
}
